import { getIsomer, listIsomersByMolecule } from '@peruselab/api-client/isomers';
import {
  doubleBondConfigurations,
  generateMoleculeDepiction,
  generateMoleculeDepictionAs
} from '@peruselab/structure-editor/chemistry/isomerDepiction';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { ChemIsomerV1Document } from '@peruselab/structure-editor/domain/isomerDocument';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { getAccessToken } from '../auth/authClient';

/** Isomers looked at per molecule; more than this and the card just crosses every such bond. */
const MOST_ISOMERS = 10;

/**
 * #1178 (owner, 2026-11-02): the Isomer a molecule card is drawn as. A Molecule fixes no E/Z, but
 * when its saved Isomers all give the same cis/trans for every double bond -- most molecules have
 * one, or several identical ones (#141: every save adds one) -- the card is drawn as that, so it
 * matches the paper: E or Z as given (#1184), crossed where left open. `undefined` (no Isomer yet,
 * Isomers that differ, too many, or they can't be read): the card crosses every double bond that
 * can be E or Z. Signed out, too: listing a molecule's Isomers isn't a public read, and its 401
 * would send a visitor on a public page to sign in.
 */
export async function moleculeCardIsomer(
  moleculeId: string,
  molecule: ChemMoleculeV1Document
): Promise<ChemIsomerV1Document | undefined> {
  if (!getAccessToken()) return undefined;
  try {
    const summaries = await listIsomersByMolecule(moleculeId);
    if (summaries.length === 0 || summaries.length > MOST_ISOMERS) return undefined;
    const isomers = await Promise.all(summaries.map((summary) => getIsomer(summary.id)));
    const [first, ...rest] = isomers.map((isomer) =>
      JSON.stringify(Object.entries(doubleBondConfigurations(molecule, isomer.document)).sort())
    );
    return rest.every((key) => key === first) ? isomers[0]!.document : undefined;
  } catch {
    return undefined;
  }
}

/** #1178/#1184: a molecule as its card (and Add to paper) draws it, in the Molecule's own atom order. */
export async function moleculeCardDepiction(
  moleculeId: string,
  molecule: ChemMoleculeV1Document,
  paperSize: PaperSize
): Promise<DrawingDocument> {
  const isomer = await moleculeCardIsomer(moleculeId, molecule);
  return isomer
    ? generateMoleculeDepictionAs(molecule, isomer, paperSize)
    : generateMoleculeDepiction(molecule, paperSize);
}
